"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import { useDebounce } from "react-use";
import { getDefaultQueryRetry, WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDropsLeaderboardPage } from "@/generated/models/ApiDropsLeaderboardPage";
import useCapacitor from "./useCapacitor";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
export enum WaveDropsLeaderboardSort {
  RANK = "RANK",
  RATING_PREDICTION = "RATING_PREDICTION",
  TREND = "TREND",
  MY_REALTIME_VOTE = "MY_REALTIME_VOTE",
  CREATED_AT = "CREATED_AT",
}
interface UseWaveDropsLeaderboardProps {
  readonly waveId: string;
  readonly connectedProfileHandle: string | null;
  readonly sort?: WaveDropsLeaderboardSort | undefined;
  readonly pausePolling?: boolean | undefined;
}
const SORT_DIRECTION_MAP: Record<WaveDropsLeaderboardSort, "ASC" | "DESC" | undefined> = {
  [WaveDropsLeaderboardSort.RANK]: undefined,
  [WaveDropsLeaderboardSort.RATING_PREDICTION]: "DESC",
  [WaveDropsLeaderboardSort.TREND]: "DESC",
  [WaveDropsLeaderboardSort.MY_REALTIME_VOTE]: undefined,
  [WaveDropsLeaderboardSort.CREATED_AT]: "DESC"
};
const POLLING_DELAY = 3000;
const ACTIVE_POLLING_INTERVAL = 5000;
const INACTIVE_POLLING_INTERVAL = 30000;
function useTabVisibility() {
  const $ = _c(2);
  const [isVisible, setIsVisible] = useState(!document.hidden);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleVisibilityChange = () => setIsVisible(!document.hidden);
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isVisible;
}
export function useWaveDropsLeaderboard(t0) {
  const $ = _c(68);
  const {
    waveId,
    connectedProfileHandle,
    sort: t1,
    pausePolling: t2
  } = t0;
  const sort = t1 === undefined ? WaveDropsLeaderboardSort.RANK : t1;
  const pausePolling = t2 === undefined ? false : t2;
  const {
    isCapacitor
  } = useCapacitor();
  const queryClient = useQueryClient();
  const [canPoll, setCanPoll] = useState(false);
  const isTabVisible = useTabVisibility();
  const pollingTimeoutRef = useRef(null);
  const sortDirection = SORT_DIRECTION_MAP[sort];
  let t3;
  if ($[0] !== sort || $[1] !== sortDirection || $[2] !== waveId) {
    t3 = [QueryKey.DROPS_LEADERBOARD, {
      waveId,
      page_size: WAVE_DROPS_PARAMS.limit,
      sort,
      sort_direction: sortDirection
    }];
    $[0] = sort;
    $[1] = sortDirection;
    $[2] = waveId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const queryKey = t3;
  let t4;
  if ($[4] !== sort) {
    t4 = lastPage => {
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        const haveZeroVotes = lastPage.drops.some(_temp);
        if (haveZeroVotes) {
          return null;
        }
      }
      return lastPage.next ? lastPage.page + 1 : null;
    };
    $[4] = sort;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const getNextPageParam = t4;
  let t5;
  if ($[6] !== getNextPageParam || $[7] !== queryClient || $[8] !== queryKey || $[9] !== sort || $[10] !== sortDirection || $[11] !== waveId) {
    t5 = () => {
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t6 => {
          const {
            pageParam
          } = t6;
          const params = {
            page_size: WAVE_DROPS_PARAMS.limit.toString(),
            sort
          };
          if (sortDirection) {
            params.sort_direction = sortDirection;
          }
          if (typeof pageParam === "number") {
            params.page = `${pageParam}`;
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/leaderboard`,
            params
          });
        },
        initialPageParam: null,
        getNextPageParam,
        pages: 1,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    $[6] = getNextPageParam;
    $[7] = queryClient;
    $[8] = queryKey;
    $[9] = sort;
    $[10] = sortDirection;
    $[11] = waveId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== sort || $[14] !== waveId) {
    t6 = [waveId, sort];
    $[13] = sort;
    $[14] = waveId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== sort || $[17] !== sortDirection || $[18] !== waveId) {
    t7 = async t8 => {
      const {
        pageParam: pageParam_0
      } = t8;
      const params_0 = {
        page_size: WAVE_DROPS_PARAMS.limit.toString(),
        sort
      };
      if (sortDirection) {
        params_0.sort_direction = sortDirection;
      }
      if (typeof pageParam_0 === "number") {
        params_0.page = `${pageParam_0}`;
      }
      return await commonApiFetch({
        endpoint: `waves/${waveId}/leaderboard`,
        params: params_0
      });
    };
    $[16] = sort;
    $[17] = sortDirection;
    $[18] = waveId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = !!connectedProfileHandle && !pausePolling;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = getDefaultQueryRetry();
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== getNextPageParam || $[22] !== queryKey || $[23] !== t7 || $[24] !== t8) {
    t10 = {
      queryKey,
      queryFn: t7,
      initialPageParam: null,
      getNextPageParam,
      enabled: t8,
      staleTime: 60000,
      ...t9
    };
    $[21] = getNextPageParam;
    $[22] = queryKey;
    $[23] = t7;
    $[24] = t8;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t10);
  let t11;
  if ($[26] !== data || $[27] !== sort) {
    bb0: {
      if (!data?.pages) {
        let t12;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = [];
          $[29] = t12;
        } else {
          t12 = $[29];
        }
        t11 = t12;
        break bb0;
      }
      const mappedDrops = mapToExtendedDrops(data.pages.map(_temp2), []);
      const uniqueDrops = generateUniqueKeys(mappedDrops, []);
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        t11 = uniqueDrops.filter(_temp3);
        break bb0;
      }
      t11 = uniqueDrops;
    }
    $[26] = data;
    $[27] = sort;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const drops = t11;
  const hasInitialized = !!data?.pages;
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setCanPoll(true);
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== data) {
    t13 = [data];
    $[31] = data;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  useDebounce(t12, 10000, t13);
  let t14;
  if ($[33] !== drops) {
    t14 = drops.some(_temp4);
    $[33] = drops;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const hasTempDrop = t14;
  const canAutoRefetch = isTabVisible && !hasTempDrop;
  let t15;
  if ($[35] !== drops) {
    t15 = pollingData => {
      if (pollingData.drops.length === 0 || drops.length === 0) {
        return false;
      }
      const latestPolledDrop = pollingData.drops[0];
      const newestExistingTimestamp = Math.max(...drops.map(_temp5));
      if (latestPolledDrop) {
        const polledCreatedAt = new Date(latestPolledDrop.created_at).getTime();
        return polledCreatedAt > newestExistingTimestamp;
      }
      return true;
    };
    $[35] = drops;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const checkForNewDrops = t15;
  let t16;
  if ($[37] !== queryKey) {
    t16 = [...queryKey, "polling"];
    $[37] = queryKey;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== canAutoRefetch || $[40] !== checkForNewDrops || $[41] !== refetch || $[42] !== waveId) {
    t17 = async () => {
      const params_1 = {
        page_size: "1",
        sort: WaveDropsLeaderboardSort.CREATED_AT,
        sort_direction: "DESC"
      };
      const result = await commonApiFetch({
        endpoint: `waves/${waveId}/leaderboard`,
        params: params_1
      });
      if (canAutoRefetch && checkForNewDrops(result)) {
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
        }
        pollingTimeoutRef.current = setTimeout(() => {
          refetch();
        }, POLLING_DELAY);
      }
      return result;
    };
    $[39] = canAutoRefetch;
    $[40] = checkForNewDrops;
    $[41] = refetch;
    $[42] = waveId;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const t18 = canPoll && !pausePolling;
  const t19 = isTabVisible ? ACTIVE_POLLING_INTERVAL : INACTIVE_POLLING_INTERVAL;
  const t20 = !isCapacitor;
  let t21;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = getDefaultQueryRetry();
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== checkForNewDrops || $[46] !== t16 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t20) {
    t22 = {
      queryKey: t16,
      queryFn: t17,
      select: checkForNewDrops,
      enabled: t18,
      refetchInterval: t19,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchIntervalInBackground: t20,
      ...t21
    };
    $[45] = checkForNewDrops;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  const {
    data: t23
  } = useQuery(t22);
  const haveNewDrops = t23 === undefined ? false : t23;
  let t24;
  let t25;
  if ($[52] !== queryClient || $[53] !== waveId) {
    t24 = () => () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
      queryClient.removeQueries({
        queryKey: [QueryKey.DROPS, {
          waveId
        }]
      });
    };
    t25 = [waveId, queryClient];
    $[52] = queryClient;
    $[53] = waveId;
    $[54] = t24;
    $[55] = t25;
  } else {
    t24 = $[54];
    t25 = $[55];
  }
  useEffect(t24, t25);
  let t26;
  if ($[56] !== fetchNextPage || $[57] !== hasNextPage) {
    t26 = async () => {
      if (hasNextPage) {
        await fetchNextPage();
      }
    };
    $[56] = fetchNextPage;
    $[57] = hasNextPage;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const manualFetch = t26;
  const t27 = isFetching || !hasInitialized;
  let t28;
  if ($[59] !== drops || $[60] !== fetchNextPage || $[61] !== hasNextPage || $[62] !== haveNewDrops || $[63] !== isFetchingNextPage || $[64] !== manualFetch || $[65] !== refetch || $[66] !== t27) {
    t28 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isFetching: t27,
      isFetchingNextPage,
      refetch,
      haveNewDrops,
      manualFetch
    };
    $[59] = drops;
    $[60] = fetchNextPage;
    $[61] = hasNextPage;
    $[62] = haveNewDrops;
    $[63] = isFetchingNextPage;
    $[64] = manualFetch;
    $[65] = refetch;
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  return t28;
}
function _temp5(drop_2) {
  return new Date(drop_2.created_at).getTime();
}
function _temp4(drop_1) {
  return drop_1.id.startsWith("temp-");
}
function _temp3(drop_0) {
  return drop_0.context_profile_context?.rating !== 0;
}
function _temp2(page) {
  return {
    wave: page.wave,
    drops: page.drops
  };
}
function _temp(drop) {
  return drop.context_profile_context?.rating === 0;
}